// ** React Imports
import { useState, ReactNode, useContext } from 'react'

// ** Next Imports
import Link from 'next/link'

// ** MUI Components\
import { styled } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CardContent from '@mui/material/CardContent'
import FormControl from '@mui/material/FormControl'
import FormHelperText from '@mui/material/FormHelperText'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import InputLabel from '@mui/material/InputLabel'
import MuiCard, { CardProps } from '@mui/material/Card'
import MuiLink from '@mui/material/Link'
import OutlinedInput from '@mui/material/OutlinedInput'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

// ** Icons Imports
import EyeOutline from 'mdi-material-ui/EyeOutline'
import EyeOffOutline from 'mdi-material-ui/EyeOffOutline'

// ** Third Party Imports
import * as yup from 'yup'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

// ** Hooks
import { AuthContext } from 'src/context/AuthContext'

// ** Configs
import themeConfig from 'src/configs/themeConfig'

// ** Layout Import
import BlankLayoutAppBar from 'src/layouts/BlankLayoutWithAppBar'

// ** Custom Components Imports
import myToast from 'src/components/myToast'

import { setup } from 'src/lib/csrf'

// ** Styled Components
const Card = styled(MuiCard)<CardProps>(({ theme }) => ({
  [theme.breakpoints.up('sm')]: { width: 500 }
}))

const schema = yup.object().shape({
  username: yup
    .string()
    .min(5, 'Username minimal terdiri dari 5 karakter')
    .required('Username wajib diisi')
    .matches(/^\S+$/, 'Username tidak boleh mengandung spasi'),

  password: yup.string().required('Password harus diisi')
})

const defaultValues: { username: string; password: string } = {
  username: '',
  password: ''
}

interface FormData {
  username: string
  password: string
}

const LoginPage = () => {
  const [showPassword, setShowPassword] = useState<boolean>(false)

  // ** Hooks
  const auth = useContext(AuthContext)

  const StyledImg = styled('img')(() => ({
    height: 60
  }))

  const {
    control,
    setError,
    handleSubmit,
    formState: { errors }
  } = useForm({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(schema)
  })

  const onSubmit = (data: FormData) => {
    const { username, password } = data

    const myPromise = new Promise<string>((resolve, reject) => {
      auth.login(
        { username, password },
        () => {
          resolve('OK')
        },
        message => {
          const errMessage = message.toString().toLowerCase().includes('locked')
            ? 'Akun anda terkunci. Silahkan gunakan fitur【Unlock Account】'
            : message
          setError('username', { type: 'manual' })
          setError('password', { type: 'manual', message: errMessage })
          reject(errMessage)
        }
      )
    })

    myToast(myPromise, 'Login Berhasil', 'Menuju Ke Dashboard Anda', 'Login Gagal')
  }

  return (
    <Box className='content-center'>
      <Card sx={{ zIndex: 1 }}>
        <CardContent sx={{ px: 8, py: 16 }}>
          <Box sx={{ mb: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <StyledImg
              src={`${process.env.NEXT_PUBLIC_BASE_PATH}/images/logo/logo-tittle-hor.svg`}
              alt='erecrutment Bankaltimtara'
            />
          </Box>
          <Box sx={{ mb: 6 }}>
            <Typography variant='body1' textAlign='center' sx={{ mb: 1, fontWeight: 600, letterSpacing: 0 }}>
              {`Selamat datang di ${themeConfig.templateName} Bankaltimtara!`}
            </Typography>
            <Typography variant='body2' textAlign='center' sx={{ letterSpacing: 0 }}>
              Silahkan masuk untuk mengikuti rekrutmen kami.
            </Typography>
          </Box>
          <form noValidate autoComplete='off' onSubmit={handleSubmit(onSubmit)}>
            <FormControl fullWidth>
              <Controller
                name='username'
                control={control}
                rules={{ required: true }}
                render={({ field: { value, onChange, onBlur } }) => (
                  <TextField
                    autoFocus
                    label='Username'
                    autoComplete='off'
                    value={value}
                    onBlur={onBlur}
                    onChange={onChange}
                    error={Boolean(errors.username)}
                  />
                )}
              />
              <Box sx={{ minHeight: 20, mb: 1 }}>
                {errors.username ? (
                  <FormHelperText sx={{ color: 'error.main', fontSize: '0.7rem', mt: 0, fontWeight: 600 }}>
                    {errors.username.message}
                  </FormHelperText>
                ) : null}
              </Box>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel htmlFor='auth-login-v2-password' error={Boolean(errors.password)}>
                Password
              </InputLabel>
              <Controller
                name='password'
                control={control}
                rules={{ required: true }}
                render={({ field: { value, onChange, onBlur } }) => (
                  <OutlinedInput
                    value={value}
                    onBlur={onBlur}
                    label='Password'
                    onChange={onChange}
                    id='auth-login-v2-password'
                    autoComplete='off'
                    error={Boolean(errors.password)}
                    type={showPassword ? 'text' : 'password'}
                    endAdornment={
                      <InputAdornment position='end'>
                        <IconButton
                          edge='end'
                          onMouseDown={e => e.preventDefault()}
                          onClick={() => setShowPassword(!showPassword)}
                          color={Boolean(errors.password) ? `error` : undefined}
                        >
                          {showPassword ? <EyeOutline /> : <EyeOffOutline />}
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                )}
              />
              <Box sx={{ minHeight: 20, mb: 1 }}>
                {errors.password ? (
                  <FormHelperText sx={{ color: 'error.main', fontSize: '0.7rem', mt: 0, fontWeight: 600 }} id=''>
                    {errors.password.message}
                  </FormHelperText>
                ) : null}
              </Box>
            </FormControl>

            <Box display='flex' justifyContent='center'>
              <Button
                size='large'
                type='submit'
                variant='contained'
                sx={{ mb: 8, textTransform: 'none', borderRadius: 4, px: '70px !important' }}
              >
                Login
              </Button>
            </Box>
          </form>
          {/* <Divider /> */}
          <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
            {/* <Link passHref href='/unlock-account'>
              <Typography component={MuiLink} variant='body2' sx={{ color: 'primary.main' }}>
                Unlock Account
              </Typography>
            </Link> */}
            <Link passHref href='/forgot-password'>
              <Typography component={MuiLink} variant='body2' sx={{ color: 'primary.main' }}>
                Lupa Username/Password?
              </Typography>
            </Link>
          </Box>
        </CardContent>
      </Card>
    </Box>
  )
}

LoginPage.getLayout = (page: ReactNode) => <BlankLayoutAppBar>{page}</BlankLayoutAppBar>

LoginPage.guestGuard = true
LoginPage.authGuard = false

export const getServerSideProps = setup(async () => {
  return { props: {} }
})

export default LoginPage
